/**
 * Stytch Authentication Hook
 * Implements authentication using Stytch and Xano dual auth
 */

import { useState, useEffect, useCallback } from 'react';
import { useStytch, useStytchSession, useStytchUser } from '@stytch/react';
import { dualAuthService } from '../../../services/auth/dualAuthService';
import { stytchService } from '../../../services/auth/stytchService';
import apiClient, { stripeClient, examsClient } from '../../../api/client';
import { getRoleFromId, USER_ROLES } from '../../../shared/constants';
import type { User } from '../../../api/auth/schemas/auth';
import type { DualAuthSession } from '../../../stytch';

interface StytchAuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  session: DualAuthSession | null;
}

export const useStytchAuth = () => {
  const stytch = useStytch();
  const { session: stytchSession } = useStytchSession();
  const { user: stytchUser } = useStytchUser();
  
  const [authState, setAuthState] = useState<StytchAuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    session: null,
  });
  
  // Initialize auth state from existing sessions
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for SSO authentication first (Xano-only, no Stytch session)
        const ssoAuthenticated = localStorage.getItem('sso_authenticated');
        const xanoToken = localStorage.getItem('xano_auth_token');

        if (ssoAuthenticated === 'true' && xanoToken) {
          // SSO flow - validate and use Xano token directly
          const { xanoAuthService } = await import('../../../api/services/xanoAuth');
          const isValid = await xanoAuthService.validateToken(xanoToken);

          if (isValid) {
            const xanoUser = await xanoAuthService.getCurrentUser(xanoToken);

            let role: string = USER_ROLES.USER;
            if (xanoUser.user_role) {
              role = getRoleFromId(xanoUser.user_role);
            } else if (xanoUser._user_roles?.role) {
              role = xanoUser._user_roles.role;
            }

            const user: User = {
              id: xanoUser.id.toString(),
              email: xanoUser.email,
              firstName: xanoUser.first_name || '',
              lastName: xanoUser.last_name || '',
              role: role,
              isActive: true,
              isEmailVerified: xanoUser.email_verified || false,
              lastLoginAt: new Date().toISOString(),
              profileImageUrl: null,
              onboarding_completed: xanoUser.onboarding_completed || false,
              subscription_status: xanoUser.subscription_status || 'pending',
              subscription_tier: xanoUser.subscription_tier || null,
              subscription_start_date: xanoUser.subscription_start_date || null,
              subscription_end_date: xanoUser.subscription_end_date || null,
              stripe_customer_id: xanoUser.stripe_customer_id || null,
              stripe_subscription_id: xanoUser.stripe_subscription_id || null,
              is_grandfathered: xanoUser.is_grandfathered || false,
              daily_question_limit: xanoUser.daily_question_limit || 10,
              questions_used_today: xanoUser.questions_used_today || 0,
              last_question_reset: xanoUser.last_question_reset || null,
              active_exam_count: xanoUser.active_exam_count || 0,
              preferences: {
                theme: 'system',
                notifications: {
                  email: true,
                  push: true,
                  reviewAssignments: true,
                  budgetWarnings: true,
                },
                timezone: xanoUser.timezone || 'UTC',
                language: 'en',
              },
              createdAt: xanoUser.created_at,
              updatedAt: xanoUser.updated_at || new Date().toISOString(),
            };

            apiClient.setAuthToken(xanoToken);
            stripeClient.setAuthToken(xanoToken);
            examsClient.setAuthToken(xanoToken);

            setAuthState({
              user,
              isAuthenticated: true,
              isLoading: false,
              session: { stytchSession: null, xanoToken, isAuthenticated: true, isLoading: false },
            });
            return;
          } else {
            // Invalid SSO token, clear it
            localStorage.removeItem('sso_authenticated');
            localStorage.removeItem('sso_user');
          }
        }

        // Check for existing dual session (Stytch + Xano)
        const existingSession = dualAuthService.getCurrentSession();

        if (existingSession && existingSession.xanoToken) {
          // Validate Xano token
          const { xanoAuthService } = await import('../../../api/services/xanoAuth');
          const isValid = await xanoAuthService.validateToken(existingSession.xanoToken);
          
          if (isValid) {
            // Get user data
            const xanoUser = await xanoAuthService.getCurrentUser(existingSession.xanoToken);
            
            // Map to our User type using centralized role definitions
            let role: string = USER_ROLES.USER; // Default role
            
            // Map role from Xano user data
            if (xanoUser.user_role) {
              role = getRoleFromId(xanoUser.user_role);
            } else if (xanoUser._user_roles?.role) {
              // Fallback to nested role object if it exists
              role = xanoUser._user_roles.role;
            }
            
            
            const user: User = {
              id: xanoUser.id.toString(),
              email: xanoUser.email,
              firstName: xanoUser.first_name || '',
              lastName: xanoUser.last_name || '',
              role: role,
              isActive: true,
              isEmailVerified: stytchUser?.emails?.[0]?.verified || xanoUser.email_verified || false,
              lastLoginAt: new Date().toISOString(),
              profileImageUrl: null,
              onboarding_completed: xanoUser.onboarding_completed || false,
              subscription_status: xanoUser.subscription_status || 'pending',
              subscription_tier: xanoUser.subscription_tier || null,
              subscription_start_date: xanoUser.subscription_start_date || null,
              subscription_end_date: xanoUser.subscription_end_date || null,
              stripe_customer_id: xanoUser.stripe_customer_id || null,
              stripe_subscription_id: xanoUser.stripe_subscription_id || null,
              is_grandfathered: xanoUser.is_grandfathered || false,
              daily_question_limit: xanoUser.daily_question_limit || 10,
              questions_used_today: xanoUser.questions_used_today || 0,
              last_question_reset: xanoUser.last_question_reset || null,
              active_exam_count: xanoUser.active_exam_count || 0,
              preferences: {
                theme: 'system',
                notifications: {
                  email: true,
                  push: true,
                  reviewAssignments: true,
                  budgetWarnings: true,
                },
                timezone: xanoUser.timezone || 'UTC',
                language: 'en',
              },
              createdAt: xanoUser.created_at,
              updatedAt: xanoUser.updated_at || new Date().toISOString(),
            };
            
            // Set API client token for all clients
            apiClient.setAuthToken(existingSession.xanoToken);
            stripeClient.setAuthToken(existingSession.xanoToken);
            examsClient.setAuthToken(existingSession.xanoToken);

            setAuthState({
              user,
              isAuthenticated: true,
              isLoading: false,
              session: existingSession,
            });
            return;
          }
        }
        
        // No valid session
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          session: null,
        });
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          session: null,
        });
      }
    };
    
    initializeAuth();
  }, [stytchSession, stytchUser]);
  
  // Send magic link for login/signup
  const sendMagicLink = useCallback(async (email: string) => {
    try {
      await dualAuthService.sendMagicLink(email);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send magic link');
    }
  }, []);
  
  // Start OAuth flow
  const startOAuthFlow = useCallback(async (provider: 'google' | 'microsoft' | 'apple') => {
    try {
      await dualAuthService.startOAuthFlow(provider);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to start OAuth login');
    }
  }, []);
  
  
  // Authenticate magic link
  const authenticateMagicLink = useCallback(async (token: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const { user, session } = await dualAuthService.authenticateWithMagicLink(token);

      // Set API client token for all clients
      apiClient.setAuthToken(session.xanoToken!);
      stripeClient.setAuthToken(session.xanoToken!);
      examsClient.setAuthToken(session.xanoToken!);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        session,
      });
    } catch (error: any) {
      console.error('useStytchAuth: Authentication failed', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw new Error(error.message || 'Failed to authenticate magic link');
    }
  }, []);
  
  // Authenticate OAuth
  const authenticateOAuth = useCallback(async (token: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const { user, session } = await dualAuthService.authenticateWithOAuth(token);

      // Set API client token for all clients
      apiClient.setAuthToken(session.xanoToken!);
      stripeClient.setAuthToken(session.xanoToken!);
      examsClient.setAuthToken(session.xanoToken!);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        session,
      });
    } catch (error: any) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw new Error(error.message || 'Failed to authenticate OAuth');
    }
  }, []);
  
  
  // Logout
  const logout = useCallback(async () => {
    try {
      await dualAuthService.logout();

      // Clear SSO data
      localStorage.removeItem('sso_authenticated');
      localStorage.removeItem('sso_user');

      // Clear API client token for all clients
      apiClient.setAuthToken(undefined);
      stripeClient.setAuthToken(undefined);
      examsClient.setAuthToken(undefined);

      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        session: null,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Clear SSO data even on error
      localStorage.removeItem('sso_authenticated');
      localStorage.removeItem('sso_user');
      // Force clear state even on error
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        session: null,
      });
    }
  }, []);

  // Refetch user data (for subscription updates, etc.)
  const refetch = useCallback(async () => {
    try {
      // Check for SSO auth first
      const ssoAuthenticated = localStorage.getItem('sso_authenticated');
      let xanoToken = localStorage.getItem('xano_auth_token');

      // Fall back to dual session if not SSO
      if (!ssoAuthenticated || !xanoToken) {
        const existingSession = dualAuthService.getCurrentSession();
        if (!existingSession || !existingSession.xanoToken) {
          return;
        }
        xanoToken = existingSession.xanoToken;
      }

      if (!xanoToken) {
        return;
      }

      const { xanoAuthService } = await import('../../../api/services/xanoAuth');
      const xanoUser = await xanoAuthService.getCurrentUser(xanoToken);

      // Map role
      let role: string = USER_ROLES.USER;
      if (xanoUser.user_role) {
        role = getRoleFromId(xanoUser.user_role);
      } else if (xanoUser._user_roles?.role) {
        role = xanoUser._user_roles.role;
      }

      const user: User = {
        id: xanoUser.id.toString(),
        email: xanoUser.email,
        firstName: xanoUser.first_name || '',
        lastName: xanoUser.last_name || '',
        role: role,
        isActive: true,
        isEmailVerified: xanoUser.email_verified || false,
        lastLoginAt: new Date().toISOString(),
        profileImageUrl: null,
        onboarding_completed: xanoUser.onboarding_completed || false,
        subscription_status: xanoUser.subscription_status || 'pending',
        subscription_tier: xanoUser.subscription_tier || null,
        subscription_start_date: xanoUser.subscription_start_date || null,
        subscription_end_date: xanoUser.subscription_end_date || null,
        stripe_customer_id: xanoUser.stripe_customer_id || null,
        stripe_subscription_id: xanoUser.stripe_subscription_id || null,
        is_grandfathered: xanoUser.is_grandfathered || false,
        daily_question_limit: xanoUser.daily_question_limit || 10,
        questions_used_today: xanoUser.questions_used_today || 0,
        last_question_reset: xanoUser.last_question_reset || null,
        active_exam_count: xanoUser.active_exam_count || 0,
        preferences: {
          theme: 'system',
          notifications: {
            email: true,
            push: true,
            reviewAssignments: true,
            budgetWarnings: true,
          },
          timezone: xanoUser.timezone || 'UTC',
          language: 'en',
        },
        createdAt: xanoUser.created_at,
        updatedAt: xanoUser.updated_at || new Date().toISOString(),
      };

      setAuthState(prev => ({
        ...prev,
        user,
      }));
    } catch (error) {
      console.error('Failed to refetch user data:', error);
    }
  }, []);

  return {
    ...authState,
    sendMagicLink,
    startOAuthFlow,
    authenticateMagicLink,
    authenticateOAuth,
    logout,
    refetch,

    // Compatibility methods for AuthContext
    login: async (email: string) => { await sendMagicLink(email); },
    signup: async (email: string) => { await sendMagicLink(email); },
    sendOTP: async () => { throw new Error('OTP not implemented'); },
    verifyOTP: async () => { throw new Error('OTP not implemented'); },
  };
};